import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { GraduationCap, KeyRound, ChevronLeft, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const [mode, setMode] = useState(null); // "teacher" or "code"
  const [name, setName] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, loginWithCode } = useAuth();
  const navigate = useNavigate();

  const handleTeacherLogin = async () => {
    if (!name.trim()) return;
    setLoading(true);
    setError("");
    try {
      await login(name.trim(), "teacher");
      navigate("/home");
    } catch (err) {
      setError("Erreur de connexion");
      console.error("Login failed:", err);
    }
    setLoading(false);
  };

  const handleCodeLogin = async () => {
    if (!accessCode.trim()) return;
    setLoading(true);
    setError("");
    try {
      await loginWithCode(accessCode.trim().toUpperCase());
      navigate("/home");
    } catch (err) {
      setError("Code d'accès invalide");
      console.error("Code login failed:", err);
    }
    setLoading(false);
  };

  const handleBack = () => {
    setMode(null);
    setName("");
    setAccessCode("");
    setError("");
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-blue-50 flex flex-col"
    >
      {/* Header */}
      <header className="w-full bg-white/80 backdrop-blur-lg border-b border-orange-100 shadow-sm py-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/NaviLogo.png" 
                alt="L'École de Navi" 
                className="h-10 lg:h-12 w-auto drop-shadow-md"
              />
              <span className="font-fredoka font-bold text-xl text-slate-800 hidden sm:block">
                L'École de Navi
              </span>
            </Link>
            <Link 
              to="/"
              className="text-slate-600 hover:text-orange-500 font-nunito font-medium flex items-center gap-1 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Mascotte */}
          <div className="flex justify-center mb-6">
            <img 
              src="/LEcoleDeNavi3.png"
              alt="Navi"
              className="w-28 h-auto drop-shadow-lg mascot-float"
            />
          </div>

          {/* Card */}
          <div className="bg-white border-2 border-orange-100 rounded-3xl shadow-xl p-8">
            
            {/* Mode Selection */}
            {mode === null && (
              <div className="space-y-6">
                <div className="text-center">
                  <h1
                    data-testid="login-title"
                    className="font-fredoka text-3xl font-bold text-slate-800 mb-2"
                  >
                    Bienvenue !
                  </h1>
                  <p className="text-slate-500 font-nunito">
                    Comment souhaitez-vous vous connecter ?
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Teacher Login */}
                  <button
                    data-testid="mode-teacher-btn"
                    onClick={() => setMode("teacher")}
                    className="w-full flex items-center gap-4 p-5 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-300 rounded-2xl transition-all group"
                  >
                    <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center shadow-[0_4px_0_rgb(29,78,216)] group-hover:shadow-[0_2px_0_rgb(29,78,216)] group-hover:translate-y-[2px] transition-all">
                      <GraduationCap size={28} className="text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="font-fredoka font-bold text-slate-800 text-lg block">Enseignant</span>
                      <span className="text-slate-500 text-sm font-nunito">Accéder à mon espace de création</span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                  </button>

                  {/* Code Login */}
                  <button
                    data-testid="mode-code-btn"
                    onClick={() => setMode("code")}
                    className="w-full flex items-center gap-4 p-5 bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-300 rounded-2xl transition-all group"
                  >
                    <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center shadow-[0_4px_0_rgb(22,163,74)] group-hover:shadow-[0_2px_0_rgb(22,163,74)] group-hover:translate-y-[2px] transition-all">
                      <KeyRound size={28} className="text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="font-fredoka font-bold text-slate-800 text-lg block">Élève / Parent</span>
                      <span className="text-slate-500 text-sm font-nunito">J'ai un code d'accès</span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-green-500 transition-colors" />
                  </button>
                </div>
              </div>
            )}

            {/* Teacher Login Form */}
            {mode === "teacher" && (
              <div className="space-y-6">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1 text-slate-500 hover:text-orange-500 font-nunito font-medium transition-colors"
                  data-testid="back-btn"
                >
                  <ChevronLeft size={18} />
                  Retour
                </button>

                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <GraduationCap className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-fredoka text-2xl font-bold text-slate-800 mb-1">
                    Espace Enseignant
                  </h2>
                  <p className="text-slate-500 font-nunito">Entrez votre nom pour continuer</p>
                </div>

                <div>
                  <label className="block text-sm font-fredoka font-bold text-slate-700 mb-2">
                    Votre nom
                  </label>
                  <input
                    data-testid="teacher-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleTeacherLogin()}
                    placeholder="Mme Dupont"
                    className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-lg font-nunito transition-all"
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 text-red-600 text-sm font-nunito">
                    {error}
                  </div>
                )}

                <button
                  data-testid="teacher-login-btn"
                  onClick={handleTeacherLogin}
                  disabled={loading || !name.trim()}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-fredoka font-bold py-4 rounded-xl shadow-[0_4px_0_rgb(29,78,216)] hover:shadow-[0_2px_0_rgb(29,78,216)] hover:translate-y-[2px] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_4px_0_rgb(29,78,216)] flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Entrer
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Code Login Form */}
            {mode === "code" && (
              <div className="space-y-6">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1 text-slate-500 hover:text-orange-500 font-nunito font-medium transition-colors"
                  data-testid="back-btn"
                >
                  <ChevronLeft size={18} />
                  Retour
                </button>

                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <KeyRound className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-fredoka text-2xl font-bold text-slate-800 mb-1">
                    Code d'accès
                  </h2>
                  <p className="text-slate-500 font-nunito">Entrez le code fourni par votre enseignant</p>
                </div>

                <div>
                  <label className="block text-sm font-fredoka font-bold text-slate-700 mb-2">
                    Code d'accès
                  </label>
                  <input
                    data-testid="access-code-input"
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && handleCodeLogin()}
                    placeholder="ABC123"
                    className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-lg text-center tracking-widest font-mono uppercase transition-all"
                    autoFocus
                    maxLength={10}
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 text-red-600 text-sm font-nunito">
                    {error}
                  </div>
                )}

                <button
                  data-testid="code-login-btn"
                  onClick={handleCodeLogin}
                  disabled={loading || !accessCode.trim()}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-fredoka font-bold py-4 rounded-xl shadow-[0_4px_0_rgb(22,163,74)] hover:shadow-[0_2px_0_rgb(22,163,74)] hover:translate-y-[2px] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_4px_0_rgb(22,163,74)] flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Accéder
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Help text */}
          <p className="text-center text-sm text-slate-500 mt-6 font-nunito">
            Besoin d'aide ? <a href="#" className="text-orange-500 hover:text-orange-600 font-medium">Contactez-nous</a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-slate-500 font-nunito">
        © 2024 L'École de Navi. Tous droits réservés.
      </footer>
    </div>
  );
}
